import type { ReactNode } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch } from '@artstorefronts/ui';

/**
 * HOW A MEMBER HEARS ABOUT IT (#4118) — the channel rows the bounty editor and the quest
 * editor share, so a recurring bounty and a recurring quest are set up the same way: each
 * channel's switch, what it does, and — when it recurs — how often it reminds.
 */

export type Reminder = 'first' | 'daily' | 'weekly' | 'monthly';

export const REMINDER_LABEL: Record<Reminder, string> = {
  first: 'First time only',
  daily: 'Every day',
  weekly: 'Every week',
  monthly: 'Every month',
};

/**
 * How often one channel comes back while the bounty or recurring quest is not done. Unset
 * follows its own period, so picking that period stores nothing.
 */
export function ReminderSelect({
  channel,
  value,
  cadence,
  onChange,
  testIdPrefix = 'bounty',
}: {
  channel: 'notify' | 'email';
  value: Reminder | null;
  /** The bounty's or quest's own period — picking it stores nothing (null). */
  cadence: 'daily' | 'weekly' | 'monthly';
  onChange: (value: Reminder | null) => void;
  testIdPrefix?: string;
}) {
  return (
    <Select value={value ?? cadence} onValueChange={(next) => onChange(next === cadence ? null : (next as Reminder))}>
      <SelectTrigger
        className="h-8 w-40 text-xs"
        data-testid={`${testIdPrefix}-${channel}-reminder`}
        aria-label="Reminders"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(Object.keys(REMINDER_LABEL) as Reminder[]).map((reminder) => (
          <SelectItem key={reminder} value={reminder}>
            {REMINDER_LABEL[reminder]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** One way a member hears about it: the switch, what it does, and — when it recurs — its reminders. */
export function ChannelRow({
  title,
  detail,
  checked,
  onCheckedChange,
  reminder,
  testId,
  disabled,
}: {
  title: string;
  detail: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  reminder?: ReactNode;
  testId: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-start gap-3 py-2.5" data-testid={testId}>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={title}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-xs text-gray-500">{detail}</p>
      </div>
      {checked && reminder ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Reminders</span>
          {reminder}
        </div>
      ) : null}
    </div>
  );
}
